/**
 * Client-side file validation helpers for the Syntiro Honey upload flow.
 *
 * The backend remains the authority on what it accepts — these checks only
 * reject obviously unsupported selections early, with a clear message.
 */

/** File extensions the Honey backend accepts (`.zip` is expanded server-side). */
export const ACCEPTED_FILE_EXTENSIONS: ReadonlyArray<string> = [
  '.xlsx',
  '.xlsm',
  '.xls',
  '.pdf',
  '.pptx',
  '.ppt',
  '.png',
  '.jpg',
  '.jpeg',
  '.zip'
];

/** Value for the file input `accept` attribute. */
export const FILE_INPUT_ACCEPT: string = ACCEPTED_FILE_EXTENSIONS.join(',');

/** Maximum accepted size per file (50 MB), matching the backend limit. */
export const MAX_FILE_SIZE_BYTES: number = 50 * 1024 * 1024;

/** Human-readable form of the per-file size limit. */
export const MAX_FILE_SIZE_LABEL = '50 MB';

/** Outcome of validating a set of candidate files against the upload rules. */
export interface FileValidationResult {
  /** Files that passed every client-side check, in selection order. */
  accepted: Array<File>;
  /** One human-readable rejection message per refused file. */
  rejected: Array<string>;
}

/**
 * Check whether a filename carries one of the accepted extensions (case-insensitive).
 * @param {string} filename Original filename including extension.
 * @returns {boolean}
 */
export const hasAcceptedExtension = (filename: string): boolean => {
  const lower_name = filename.toLowerCase();
  return ACCEPTED_FILE_EXTENSIONS.some((extension: string) => lower_name.endsWith(extension));
};

/**
 * Validate candidate files against extension, size and duplicate-name rules.
 * Duplicate names are rejected because per-file context overrides are keyed
 * by filename on the wire — two files with the same name would be ambiguous.
 * @param {Array<File>} candidates Newly picked/dropped files.
 * @param {Array<string>} existing_names Filenames already selected in the batch.
 * @returns {FileValidationResult} Accepted files plus per-file rejection messages.
 */
export const validateFiles = (
  candidates: Array<File>,
  existing_names: Array<string>
): FileValidationResult => {
  const accepted: Array<File> = [];
  const rejected: Array<string> = [];
  const seen_names = new Set<string>(existing_names);
  for (const candidate of candidates) {
    if (!hasAcceptedExtension(candidate.name)) {
      rejected.push(
        `${candidate.name}: unsupported file type. Accepted: ${ACCEPTED_FILE_EXTENSIONS.join(', ')}.`
      );
    } else if (candidate.size > MAX_FILE_SIZE_BYTES) {
      rejected.push(
        `${candidate.name}: file is ${formatByteSize(candidate.size)} — the limit is ${MAX_FILE_SIZE_LABEL} per file.`
      );
    } else if (seen_names.has(candidate.name)) {
      rejected.push(`${candidate.name}: a file with this name is already selected.`);
    } else {
      seen_names.add(candidate.name);
      accepted.push(candidate);
    }
  }
  return { accepted, rejected };
};

/**
 * Format a byte count as a short human-readable size (e.g. "1.2 MB").
 * @param {number} bytes Size in bytes.
 * @returns {string}
 */
export const formatByteSize = (bytes: number): string => {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
